<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PhoneSettingsAPI from 'dashboard/api/phoneSettings';

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');

const loading = ref(true);
const saving = ref(false);
const failed = ref('');
const status = ref(null);
const form = ref({
  BUSINESS_HOURS_ENABLED: true,
  BUSINESS_OPEN: '09:00',
  BUSINESS_CLOSE: '17:00',
  BUSINESS_TIMEZONE: 'America/New_York',
  BUSINESS_DAYS: 'mon,tue,wed,thu,fri',
  BUSINESS_HOLIDAYS: '',
  GREETING_TEXT: '',
  VOICEMAIL_ENABLED: true,
  VOICEMAIL_PROMPT: '',
  VOICEMAIL_MAX_SECONDS: 180,
  OUTBOUND_NUMBERS: [],
});

const DAYS = [
  { key: 'mon', label: 'MON' },
  { key: 'tue', label: 'TUE' },
  { key: 'wed', label: 'WED' },
  { key: 'thu', label: 'THU' },
  { key: 'fri', label: 'FRI' },
  { key: 'sat', label: 'SAT' },
  { key: 'sun', label: 'SUN' },
];

const openDays = computed(() =>
  (form.value.BUSINESS_DAYS || '')
    .split(',')
    .map(d => d.trim().toLowerCase())
    .filter(Boolean)
);

const toggleDay = key => {
  const set = new Set(openDays.value);
  if (set.has(key)) set.delete(key);
  else set.add(key);
  form.value.BUSINESS_DAYS = DAYS.filter(d => set.has(d.key))
    .map(d => d.key)
    .join(',');
};

// Holidays are stored as a comma-separated string, which is fine for a config
// file and hostile in a form. Present them as a list.
const holidays = computed(() =>
  (form.value.BUSINESS_HOLIDAYS || '')
    .split(',')
    .map(d => d.trim())
    .filter(Boolean)
);
const newHoliday = ref('');

const addHoliday = () => {
  const value = (newHoliday.value || '').trim();
  if (!value || holidays.value.includes(value)) return;
  form.value.BUSINESS_HOLIDAYS = [...holidays.value, value].sort().join(',');
  newHoliday.value = '';
};

const removeHoliday = day => {
  form.value.BUSINESS_HOLIDAYS = holidays.value
    .filter(d => d !== day)
    .join(',');
};

const statusLabel = computed(() => {
  if (!status.value) return '';
  return status.value.open
    ? t('PHONE_SETTINGS.STATUS.OPEN')
    : t('PHONE_SETTINGS.STATUS.CLOSED');
});

const load = async () => {
  loading.value = true;
  failed.value = '';
  try {
    const { data } = await PhoneSettingsAPI.get(accountId.value);
    if (data.error) {
      failed.value = data.error;
    } else {
      Object.keys(form.value).forEach(key => {
        if (data.settings && data.settings[key] !== undefined) {
          form.value[key] = data.settings[key];
        }
      });
    }
    const { data: live } = await PhoneSettingsAPI.status(accountId.value);
    status.value = live;
  } catch (error) {
    failed.value = t('PHONE_SETTINGS.UNREACHABLE');
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  saving.value = true;
  try {
    const { data } = await PhoneSettingsAPI.update(accountId.value, form.value);
    if (data.error) {
      useAlert(data.error);
    } else {
      useAlert(t('PHONE_SETTINGS.SAVED'));
      const { data: live } = await PhoneSettingsAPI.status(accountId.value);
      status.value = live;
    }
  } catch (error) {
    useAlert(t('PHONE_SETTINGS.SAVE_FAILED'));
  } finally {
    saving.value = false;
  }
};

// ---- Outbound numbers -------------------------------------------------
const newNumber = ref('');
const newLabel = ref('');
const telnyxNums = ref(null); // account numbers, null until fetched
const telnyxFailed = ref(false); // the account list could not be fetched
const importing = ref(false);
const E164 = /^\+[1-9]\d{7,14}$/;

const cleanNumber = raw => String(raw || '').replace(/[\s\-()]/g, '');

// LiveAgent's reading of a number: +1 877 278 6039
const laPhone = p => {
  const d = String(p || '').replace(/\D/g, '');
  if (d.length === 11 && d[0] === '1') {
    return `+1 ${d.slice(1, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
  }
  return p;
};

const numbers = computed(() => form.value.OUTBOUND_NUMBERS || []);
const firstEnabledIndex = computed(() => numbers.value.findIndex(n => n.enabled));

// Import: the typed number must exist on the Telnyx account, or Telnyx
// would reject every call placed from it. Checked here, before it can be
// saved, rather than discovered on the first outbound call.
const addNumber = async () => {
  const num = cleanNumber(newNumber.value);
  if (!E164.test(num)) {
    useAlert(t('PHONE_SETTINGS.NUMBERS.INVALID'));
    return;
  }
  if (numbers.value.some(n => n.number === num)) {
    useAlert(t('PHONE_SETTINGS.NUMBERS.DUPLICATE'));
    return;
  }
  importing.value = true;
  try {
    if (!telnyxNums.value || telnyxFailed.value) await loadTelnyx();
    if (telnyxFailed.value) {
      useAlert(t('PHONE_SETTINGS.NUMBERS.CHECK_FAILED'));
      return;
    }
    if (!telnyxNums.value.some(tn => tn.number === num)) {
      useAlert(t('PHONE_SETTINGS.NUMBERS.NOT_ON_TELNYX'));
      return;
    }
    form.value.OUTBOUND_NUMBERS.push({
      number: num,
      label: newLabel.value.trim(),
      enabled: true,
    });
    newNumber.value = '';
    newLabel.value = '';
    // Persist at once. A separate Save step meant an import that looked
    // done vanished on refresh and never reached the phone panel.
    await save();
  } finally {
    importing.value = false;
  }
};

const removeNumber = async i => {
  form.value.OUTBOUND_NUMBERS.splice(i, 1);
  await save();
};

const moveNumber = (i, d) => {
  const list = form.value.OUTBOUND_NUMBERS;
  const j = i + d;
  if (j < 0 || j >= list.length) return;
  const [row] = list.splice(i, 1);
  list.splice(j, 0, row);
};

const loadTelnyx = async () => {
  try {
    const { data } = await PhoneSettingsAPI.telnyxNumbers(accountId.value);
    if (data.error) throw new Error(data.error);
    telnyxNums.value = data.numbers || [];
    telnyxFailed.value = false;
  } catch (error) {
    telnyxNums.value = [];
    telnyxFailed.value = true;
  }
};

const badgeFor = num => {
  if (!telnyxNums.value) {
    return { text: t('PHONE_SETTINGS.NUMBERS.CHECKING'), cls: 'text-n-slate-11' };
  }
  const hit = telnyxNums.value.find(tn => tn.number === num);
  if (!hit) {
    return { text: t('PHONE_SETTINGS.NUMBERS.NOT_ON_TELNYX'), cls: 'text-n-ruby-11' };
  }
  if (hit.on_bridge) {
    return { text: t('PHONE_SETTINGS.NUMBERS.THIS_LINE'), cls: 'text-n-teal-11' };
  }
  return {
    text: t('PHONE_SETTINGS.NUMBERS.OTHER_LINE', { name: hit.connection_name || '' }),
    cls: 'text-n-amber-11',
  };
};

onMounted(async () => {
  await load();
  loadTelnyx();
});
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('PHONE_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('PHONE_SETTINGS.TITLE')"
        :description="$t('PHONE_SETTINGS.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div v-if="failed" class="p-4 rounded-lg bg-n-ruby-3 text-n-ruby-11">
        {{ failed }}
      </div>

      <div v-else class="flex flex-col gap-6">
        <!-- What callers get right now, so nobody has to ring the number -->
        <div
          class="flex items-center gap-3 px-4 py-3 border rounded-lg border-n-weak bg-n-solid-1"
        >
          <span
            class="inline-block w-2 h-2 rounded-full"
            :class="status && status.open ? 'bg-n-teal-9' : 'bg-n-amber-9'"
          />
          <div>
            <div class="text-sm font-medium text-n-slate-12">
              {{ statusLabel }}
            </div>
            <div v-if="status" class="text-xs text-n-slate-11">
              {{ status.reason }} &middot; {{ status.schedule }}
            </div>
          </div>
        </div>

        <!-- 1. Availability -->
        <section class="p-4 border rounded-lg border-n-weak">
          <h3 class="mb-1 text-base font-medium text-n-slate-12">
            {{ $t('PHONE_SETTINGS.HOURS.TITLE') }}
          </h3>
          <p class="mb-4 text-sm text-n-slate-11">
            {{ $t('PHONE_SETTINGS.HOURS.HELP') }}
          </p>

          <label class="flex items-center gap-2 mb-4">
            <input v-model="form.BUSINESS_HOURS_ENABLED" type="checkbox" />
            <span class="text-sm">{{ $t('PHONE_SETTINGS.HOURS.ENABLED') }}</span>
          </label>

          <div :class="{ 'opacity-50 pointer-events-none': !form.BUSINESS_HOURS_ENABLED }">
            <div class="flex flex-wrap gap-4">
              <label class="flex flex-col text-sm">
                {{ $t('PHONE_SETTINGS.HOURS.OPEN') }}
                <input v-model="form.BUSINESS_OPEN" type="time" />
              </label>
              <label class="flex flex-col text-sm">
                {{ $t('PHONE_SETTINGS.HOURS.CLOSE') }}
                <input v-model="form.BUSINESS_CLOSE" type="time" />
              </label>
              <label class="flex flex-col flex-1 text-sm min-w-52">
                {{ $t('PHONE_SETTINGS.HOURS.TIMEZONE') }}
                <input v-model="form.BUSINESS_TIMEZONE" type="text" />
              </label>
            </div>

            <div class="mt-4">
              <span class="block mb-2 text-sm">
                {{ $t('PHONE_SETTINGS.HOURS.DAYS') }}
              </span>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="day in DAYS"
                  :key="day.key"
                  type="button"
                  class="px-3 py-1 text-xs font-medium border rounded-full"
                  :class="
                    openDays.includes(day.key)
                      ? 'bg-n-brand text-white border-n-brand'
                      : 'border-n-weak text-n-slate-11'
                  "
                  @click="toggleDay(day.key)"
                >
                  {{ day.label }}
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- 2. Holidays -->
        <section class="p-4 border rounded-lg border-n-weak">
          <h3 class="mb-1 text-base font-medium text-n-slate-12">
            {{ $t('PHONE_SETTINGS.HOLIDAYS.TITLE') }}
          </h3>
          <p class="mb-4 text-sm text-n-slate-11">
            {{ $t('PHONE_SETTINGS.HOLIDAYS.HELP') }}
          </p>

          <div class="flex gap-2 mb-3">
            <input v-model="newHoliday" type="date" />
            <NextButton
              faded
              slate
              type="button"
              :label="$t('PHONE_SETTINGS.HOLIDAYS.ADD')"
              @click="addHoliday"
            />
          </div>

          <div v-if="!holidays.length" class="text-sm text-n-slate-11">
            {{ $t('PHONE_SETTINGS.HOLIDAYS.NONE') }}
          </div>
          <div v-else class="flex flex-wrap gap-2">
            <span
              v-for="day in holidays"
              :key="day"
              class="inline-flex items-center gap-2 px-2 py-1 text-xs rounded bg-n-alpha-2 text-n-slate-12"
            >
              {{ day }}
              <button type="button" class="text-n-slate-11" @click="removeHoliday(day)">
                &times;
              </button>
            </span>
          </div>
        </section>

        <!-- 3. What callers hear -->
        <section class="p-4 border rounded-lg border-n-weak">
          <h3 class="mb-1 text-base font-medium text-n-slate-12">
            {{ $t('PHONE_SETTINGS.MESSAGES.TITLE') }}
          </h3>
          <p class="mb-4 text-sm text-n-slate-11">
            {{ $t('PHONE_SETTINGS.MESSAGES.HELP') }}
          </p>

          <label class="flex flex-col mb-4 text-sm">
            {{ $t('PHONE_SETTINGS.MESSAGES.GREETING') }}
            <textarea v-model="form.GREETING_TEXT" rows="2" />
            <span class="text-xs text-n-slate-11">
              {{ $t('PHONE_SETTINGS.MESSAGES.GREETING_HELP') }}
            </span>
          </label>

          <label class="flex items-center gap-2 mb-3">
            <input v-model="form.VOICEMAIL_ENABLED" type="checkbox" />
            <span class="text-sm">
              {{ $t('PHONE_SETTINGS.MESSAGES.VOICEMAIL_ENABLED') }}
            </span>
          </label>

          <div :class="{ 'opacity-50 pointer-events-none': !form.VOICEMAIL_ENABLED }">
            <label class="flex flex-col mb-4 text-sm">
              {{ $t('PHONE_SETTINGS.MESSAGES.VOICEMAIL') }}
              <textarea v-model="form.VOICEMAIL_PROMPT" rows="3" />
              <span class="text-xs text-n-slate-11">
                {{ $t('PHONE_SETTINGS.MESSAGES.VOICEMAIL_HELP') }}
              </span>
            </label>

            <label class="flex flex-col text-sm w-52">
              {{ $t('PHONE_SETTINGS.MESSAGES.MAX_SECONDS') }}
              <input
                v-model.number="form.VOICEMAIL_MAX_SECONDS"
                type="number"
                min="10"
                max="600"
              />
            </label>
          </div>
        </section>

        <!-- 4. Outbound numbers -->
        <section class="p-4 border rounded-lg border-n-weak">
          <h3 class="mb-1 text-base font-medium text-n-slate-12">
            {{ $t('PHONE_SETTINGS.NUMBERS.TITLE') }}
          </h3>
          <p class="mb-4 text-sm text-n-slate-11">
            {{ $t('PHONE_SETTINGS.NUMBERS.HELP') }}
          </p>

          <div v-if="!numbers.length" class="mb-3 text-sm text-n-slate-11">
            {{ $t('PHONE_SETTINGS.NUMBERS.NONE') }}
          </div>
          <table v-else class="w-full mb-3 text-sm">
            <tbody>
              <tr
                v-for="(n, i) in numbers"
                :key="n.number"
                class="border-t border-n-weak"
                :class="{ 'opacity-50': !n.enabled }"
              >
                <td class="w-6 py-2 pr-2">
                  <input
                    v-model="n.enabled"
                    type="checkbox"
                    :title="$t('PHONE_SETTINGS.NUMBERS.ENABLED')"
                    @change="save"
                  />
                </td>
                <td class="py-2 pr-3 font-medium whitespace-nowrap text-n-slate-12">
                  {{ laPhone(n.number) }}
                  <span
                    v-if="i === firstEnabledIndex"
                    class="ml-1 text-[10px] font-semibold uppercase text-n-brand"
                  >
                    {{ $t('PHONE_SETTINGS.NUMBERS.DEFAULT') }}
                  </span>
                </td>
                <td class="py-2 pr-3">
                  <input
                    v-model="n.label"
                    type="text"
                    maxlength="40"
                    class="!mb-0"
                    :placeholder="$t('PHONE_SETTINGS.NUMBERS.LABEL_PLACEHOLDER')"
                  />
                </td>
                <td class="py-2 pr-3 text-xs" :class="badgeFor(n.number).cls">
                  {{ badgeFor(n.number).text }}
                </td>
                <td class="py-2 text-right whitespace-nowrap">
                  <button
                    type="button"
                    class="px-2 text-n-slate-11"
                    :title="$t('PHONE_SETTINGS.NUMBERS.UP')"
                    @click="moveNumber(i, -1)"
                  >
                    &uarr;
                  </button>
                  <button
                    type="button"
                    class="px-2 text-n-slate-11"
                    :title="$t('PHONE_SETTINGS.NUMBERS.DOWN')"
                    @click="moveNumber(i, 1)"
                  >
                    &darr;
                  </button>
                  <button
                    type="button"
                    class="px-2 text-n-ruby-11"
                    :title="$t('PHONE_SETTINGS.NUMBERS.REMOVE')"
                    @click="removeNumber(i)"
                  >
                    &times;
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="flex flex-wrap items-start gap-2 mb-3">
            <input
              v-model="newNumber"
              type="text"
              class="!mb-0 w-44"
              placeholder="+18772786039"
            />
            <input
              v-model="newLabel"
              type="text"
              class="!mb-0 w-40"
              maxlength="40"
              :placeholder="$t('PHONE_SETTINGS.NUMBERS.LABEL_PLACEHOLDER')"
            />
            <NextButton
              type="button"
              :label="$t('PHONE_SETTINGS.NUMBERS.ADD')"
              :is-loading="importing"
              :disabled="importing"
              @click="addNumber"
            />
          </div>

          <p class="text-xs text-n-slate-11">
            {{ $t('PHONE_SETTINGS.NUMBERS.SAVE_HINT') }}
          </p>
        </section>

        <div class="flex items-center gap-3">
          <NextButton
            type="button"
            :label="$t('PHONE_SETTINGS.SAVE')"
            :is-loading="saving"
            :disabled="saving"
            @click="save"
          />
          <span class="text-xs text-n-slate-11">
            {{ $t('PHONE_SETTINGS.SAVE_HELP') }}
          </span>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
